/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { useLocation } from 'js/lib/useRouter';

import { Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { ArrowPreviousIcon } from '@coursera/cds-icons';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Submit from 'bundles/authentication-common/components/Submit';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/authentication';

export type componentProps = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
};

export type State = {
  magicLinkSent: boolean;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });
const styles = {
  container: css`
    display: grid;
    grid-gap: 24px;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 0 30px;

    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-300) auto;
    }
  `,
  buttonContainer: css`
    display: grid;
    grid-gap: 12px;
    grid-template-columns: 1fr;
    margin: 20px auto;
  `,
  navigation: css`
    margin: 12px auto 0;
    display: flex !important;
    text-align: center;
    ${typography2.actionSecondary}
  `,
  navigationIcon: css`
    margin-right: 8px;
  `,
  section: css`
    :nth-child(1) {
      grid-row: 1;
    }

    :nth-child(2) {
      grid-row: 2;
    }

    :nth-child(3) {
      grid-row: 3;
    }

    :nth-child(4) {
      grid-row: 4;
    }
  `,
  title: css`
    margin: 0;
    text-align: center;
  `,
  submit: css`
    margin-top: 12px;
    margin-bottom: 20px;
    width: 265px;
  `,
  spamText: css`
    margin-bottom: 0;
    margin-top: 12px;
    text-align: center;
  `,
  boldEmail: css`
    font-weight: 700;
  `,
};

const ExpiredLink = ({ navigate, magicLinkRedirect, className }: componentProps) => {
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const location = useLocation();

  const navigateToLogin = () => {
    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  const sendNewLink = () => {
    const email = location.query.email;
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });

    setMagicLinkSent(true);
  };

  return (
    <TrackedDiv trackingName="expired_login_link_modal" trackClicks={false} withVisibilityTracking>
      <section css={styles.container} aria-live="polite" className={className}>
        <header css={styles.section}>
          <Typography2 component="h1" variant="titleSmall" css={styles.title}>
            {_t('Your link expired')}
          </Typography2>
        </header>

        <TrackedSubmit
          css={[styles.submit, styles.section, styles.buttonContainer]}
          trackingName="send_new_link_button"
          disabled={magicLinkSent}
          onClick={sendNewLink}
        >
          {_t('Email me a new link')}
        </TrackedSubmit>
        <div css={styles.section}>
          <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.spamText}>
            {_t("Can't find your link? Check your spam folder.")}
          </Typography2>

          <LinkButton css={styles.navigation} onClick={navigateToLogin}>
            <div css={styles.navigationIcon}>
              <ArrowPreviousIcon color="interactive" size="large" />
            </div>
            {_t('All login options')}
          </LinkButton>
        </div>
      </section>
    </TrackedDiv>
  );
};

export default Retracked.createTrackedContainer<componentProps>(() => ({
  namespace: {
    page: 'expired_login_link_page',
  },
}))(ExpiredLink);
